// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Pressable, StyleSheet, TextInput, View } from 'react-native';
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';

import { Text } from '@/components/Themed';
import {
  buildMembershipDraftOptions,
  canConfirmMembershipDraft,
  hasExactMembershipNameMatch,
  shouldShowTemporaryToggle,
} from '@/components/playerMembershipDraft';
import GhostAvatar from '@/components/GhostAvatar';

export type PlayerMembershipTheme = 'blue' | 'amber';

export type AvailableMember = {
  id: string;
  name: string;
  userId?: string | null;
  isFriend?: boolean;
  /** A guest this user created earlier, offered alongside friends without needing a search. */
  isMine?: boolean;
  /** An AI opponent; only offered through the AI player picker. */
  isAi?: boolean;
  /** When the player row was created; keeps the newest of several same-named guests. */
  createdAt?: string;
};

export type ExistingPlayerMembershipRow = {
  kind: 'existing';
  rowId: string;
  playerId: string;
  name: string;
  locked: boolean;
  /** AI opponent — rendered with a ghost avatar instead of a plain name row. */
  isAi?: boolean;
};

export type DraftPlayerMembershipRow = {
  kind: 'draft';
  rowId: string;
  query: string;
  selectedPlayerId?: string;
  selectedPlayerName?: string;
  scp?: string;
  temporarySelected: boolean;
  status: 'idle' | 'pending';
};

export type PlayerMembershipRow = ExistingPlayerMembershipRow | DraftPlayerMembershipRow;

export type PlayerMembershipLabels = {
  addPlayer: string;
  removePlayer: string;
  searchPlaceholder: string;
  noResults?: string;
  confirmA11y: string;
  cancelA11y: string;
  temporaryToggleA11y: string;
  temporarySelected: string;
  createTemporary: string;
};

type Props = {
  rows: PlayerMembershipRow[];
  availableMembers: AvailableMember[];
  rowOpen: string | null;
  saving: boolean;
  maxPlayers: number;
  disableAddRow?: boolean;
  theme?: PlayerMembershipTheme;
  labels: PlayerMembershipLabels;
  onAddRow: () => void;
  onRemoveRow: (row: PlayerMembershipRow) => void;
  onConfirmRow: (rowId: string) => void;
  onToggleTemporary: (rowId: string) => void;
  onDraftQueryChange: (rowId: string, value: string) => void;
  onDraftSelectOption: (rowId: string, member: AvailableMember) => void;
  onDraftOpenChange: (rowId: string | null) => void;
  renderExistingTrailing?: (row: ExistingPlayerMembershipRow) => ReactNode;
  renderExistingActions?: (row: ExistingPlayerMembershipRow) => ReactNode;
  renderDraftInline?: (row: DraftPlayerMembershipRow) => ReactNode;
  isExistingDraggable?: (row: ExistingPlayerMembershipRow) => boolean;
  onExistingDragStart?: (row: ExistingPlayerMembershipRow, event: any) => void;
  onExistingDragEnd?: (row: ExistingPlayerMembershipRow, event: any) => void;
  onExistingMouseDown?: (row: ExistingPlayerMembershipRow, event: any) => void;
  onExistingTouchStart?: (row: ExistingPlayerMembershipRow, event: any) => void;
  compact?: boolean;
  renderExistingInline?: (row: ExistingPlayerMembershipRow) => ReactNode;
  removeBeforeInline?: boolean;
  actionsOutsideFrame?: boolean;
  showConfirmButton?: boolean;
  removeLabelMode?: 'text' | 'icon';
  showAddButton?: boolean;
  showTemporaryToggle?: boolean;
};

const themePalette = {
  blue: {
    border: '#A1D493',
    bg: '#07200D',
    dropdownBg: '#0b2a12',
    draftBg: '#07200D',
    addBorder: '#A1D493',
    addBg: '#1A3A20',
    addText: '#F5F5DC',
    text: '#F5F5DC',
    removeBg: '#1A3A20',
    removeBorder: '#A1D493',
    removeText: '#F5F5DC',
    temporaryBg: '#C8C8B0',
    temporaryBorder: '#A1D493',
    temporaryText: '#112c1b',
    temporaryActiveBg: '#A1D493',
    temporaryActiveText: '#112c1b',
  },
  amber: {
    border: '#92400e',
    bg: '#29170b',
    dropdownBg: '#1a120d',
    draftBg: '#29170b',
    addBorder: '#f59e0b',
    addBg: '#2c1a0e',
    addText: '#fcd34d',
    text: '#F5F5DC',
    removeBg: '#2c1a0e',
    removeBorder: '#f59e0b',
    removeText: '#fcd34d',
    temporaryBg: '#2f1a0a',
    temporaryBorder: '#92400e',
    temporaryText: '#fcd34d',
    temporaryActiveBg: '#f59e0b',
    temporaryActiveText: '#1a120d',
  },
} as const;

export default function PlayerMembershipEditor({
  rows,
  availableMembers,
  rowOpen,
  saving,
  maxPlayers,
  disableAddRow = false,
  theme = 'blue',
  labels,
  onAddRow,
  onRemoveRow,
  onConfirmRow,
  onToggleTemporary,
  onDraftQueryChange,
  onDraftSelectOption,
  onDraftOpenChange,
  renderExistingTrailing,
  renderExistingActions,
  renderDraftInline,
  isExistingDraggable,
  onExistingDragStart,
  onExistingDragEnd,
  onExistingMouseDown,
  onExistingTouchStart,
  compact = false,
  renderExistingInline,
  removeBeforeInline = false,
  actionsOutsideFrame = false,
  showConfirmButton = true,
  removeLabelMode = 'text',
  showAddButton = true,
  showTemporaryToggle = true,
}: Props) {
  const palette = themePalette[theme];
  const isCompact = compact || theme === 'amber';
  const useRoundDangerPill = theme === 'amber';
  const existingCount = useMemo(() => rows.filter((row) => row.kind === 'existing').length, [rows]);
  const canAdd = existingCount < maxPlayers && !disableAddRow;
  const draftBlurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDraftBlurTimeout = useCallback(() => {
    if (draftBlurTimeoutRef.current) {
      clearTimeout(draftBlurTimeoutRef.current);
      draftBlurTimeoutRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearDraftBlurTimeout();
    },
    [clearDraftBlurTimeout]
  );

  return (
    <View style={styles.container}>
      {rows.map((row) => {
        if (row.kind === 'existing') {
          const draggable = isExistingDraggable ? isExistingDraggable(row) : false;
          const dragProps = draggable
            ? ({
                draggable: true,
                onDragStart: (event: any) => onExistingDragStart?.(row, event),
                onDragEnd: (event: any) => onExistingDragEnd?.(row, event),
                onMouseDown: (event: any) => onExistingMouseDown?.(row, event),
                onTouchStart: (event: any) => onExistingTouchStart?.(row, event),
              } as any)
            : ({} as any);
          if (actionsOutsideFrame) {
            return (
              <View key={row.rowId} style={styles.rowWrap}>
                <View style={styles.frameWithActions}>
                  <View style={styles.existingInlineRow} {...dragProps}>
                    <View
                      style={[
                        styles.readonlyInput,
                        isCompact && styles.readonlyInputCompact,
                        { borderColor: palette.border, backgroundColor: palette.bg },
                      ]}
                    >
                      {row.isAi ? <GhostAvatar size={20} style={styles.aiAvatar} /> : null}
                      <Text
                        style={[
                          styles.existingText,
                          styles.existingTextReadonly,
                          isCompact && styles.existingTextCompact,
                          { color: palette.text },
                        ]}
                      >
                        {row.name}
                      </Text>
                    </View>
                    {renderExistingInline ? renderExistingInline(row) : null}
                  </View>
                  <View style={styles.draftActionsOutside} />
                </View>
                {renderExistingTrailing ? renderExistingTrailing(row) : null}
              </View>
            );
          }
          return (
            <View key={row.rowId} style={styles.rowWrap}>
              <View>
                <View
                  style={[
                    styles.existingRow,
                    isCompact && styles.existingRowCompact,
                    { borderColor: palette.border, backgroundColor: palette.bg },
                  ]}
                  {...dragProps}
                >
                  {row.isAi ? <GhostAvatar size={20} style={styles.aiAvatar} /> : null}
                  <Text style={[styles.existingText, isCompact && styles.existingTextCompact, { color: palette.text }]}>
                    {row.name}
                  </Text>
                  {!row.locked && removeBeforeInline ? (
                    <Pressable
                      style={[
                        styles.removeButton,
                        removeLabelMode === 'icon' && styles.removeButtonIcon,
                        isCompact && styles.removeButtonCompact,
                        isCompact && removeLabelMode === 'icon' && styles.removeButtonIconCompact,
                        { backgroundColor: palette.removeBg, borderColor: palette.removeBorder },
                      ]}
                      onPress={() => onRemoveRow(row)}
                      accessibilityLabel={`${labels.removePlayer}: ${row.name}`}
                    >
                      <Text
                        style={[
                          styles.removeButtonText,
                          removeLabelMode === 'icon' && styles.removeButtonTextIcon,
                          isCompact && !useRoundDangerPill && styles.removeButtonTextCompact,
                          { color: palette.removeText },
                        ]}
                      >
                        {removeLabelMode === 'icon' ? 'X' : labels.removePlayer}
                      </Text>
                    </Pressable>
                  ) : null}
                  {renderExistingActions ? renderExistingActions(row) : null}
                  {renderExistingInline ? renderExistingInline(row) : null}
                  {!row.locked && !removeBeforeInline ? (
                    <Pressable
                      style={[
                        styles.removeButton,
                        removeLabelMode === 'icon' && styles.removeButtonIcon,
                        isCompact && styles.removeButtonCompact,
                        isCompact && removeLabelMode === 'icon' && styles.removeButtonIconCompact,
                        { backgroundColor: palette.removeBg, borderColor: palette.removeBorder },
                      ]}
                      onPress={() => onRemoveRow(row)}
                      accessibilityLabel={`${labels.removePlayer}: ${row.name}`}
                    >
                      <Text
                        style={[
                          styles.removeButtonText,
                          removeLabelMode === 'icon' && styles.removeButtonTextIcon,
                          isCompact && !useRoundDangerPill && styles.removeButtonTextCompact,
                          { color: palette.removeText },
                        ]}
                      >
                        {removeLabelMode === 'icon' ? 'X' : labels.removePlayer}
                      </Text>
                    </Pressable>
                  ) : null}
                </View>
              </View>
              {renderExistingTrailing ? renderExistingTrailing(row) : null}
            </View>
          );
        }

        const hasExactMatch = hasExactMembershipNameMatch(availableMembers, row.query);
        const options = buildMembershipDraftOptions(
          rows,
          row.rowId,
          availableMembers,
          row.query,
          row.selectedPlayerId
        );
        const canShowTemporaryToggle = showTemporaryToggle && shouldShowTemporaryToggle(row, hasExactMatch);
        const canConfirm = canConfirmMembershipDraft(row, saving, hasExactMatch);

        return (
          <View key={row.rowId} style={styles.rowWrap}>
            <View style={actionsOutsideFrame ? styles.frameWithActions : undefined}>
              <View style={[styles.draftInputWrap, actionsOutsideFrame && styles.draftInputWrapOutside]}>
                <TextInput
                  value={row.query}
                  onChangeText={(value) => onDraftQueryChange(row.rowId, value)}
                  onSubmitEditing={() => onConfirmRow(row.rowId)}
                  placeholder={labels.searchPlaceholder}
                  placeholderTextColor="#8FA08B"
                  style={[
                    styles.draftInput,
                    isCompact && styles.draftInputCompact,
                    { borderColor: palette.border, backgroundColor: palette.draftBg, color: palette.text },
                    row.status === 'pending' && styles.disabled,
                    actionsOutsideFrame && styles.membershipFrame,
                    actionsOutsideFrame && styles.draftInputWithInline,
                    actionsOutsideFrame && styles.draftInputOutside,
                  ]}
                  editable={row.status !== 'pending'}
                  onFocus={() => {
                    clearDraftBlurTimeout();
                    onDraftOpenChange(row.rowId);
                  }}
                  onPressIn={() => {
                    clearDraftBlurTimeout();
                    onDraftOpenChange(row.rowId);
                  }}
                  onBlur={() => {
                    clearDraftBlurTimeout();
                    draftBlurTimeoutRef.current = setTimeout(() => {
                      draftBlurTimeoutRef.current = null;
                      onDraftOpenChange(null);
                    }, 220);
                  }}
                />
                {renderDraftInline ? renderDraftInline(row) : null}
              </View>
              <View style={[styles.draftActions, actionsOutsideFrame && styles.draftActionsOutside]}>
                {showConfirmButton ? (
                  <Pressable
                    style={[styles.confirmButton, !canConfirm && styles.disabled]}
                    disabled={!canConfirm}
                    onPress={() => onConfirmRow(row.rowId)}
                    accessibilityLabel={labels.confirmA11y}
                  >
                    <Text style={styles.confirmButtonText}>✓</Text>
                  </Pressable>
                ) : null}
                <Pressable
                  style={styles.cancelButton}
                  onPress={() => onRemoveRow(row)}
                  accessibilityLabel={labels.cancelA11y}
                  disabled={row.status === 'pending'}
                >
                  <Text style={styles.cancelButtonText}>✕</Text>
                </Pressable>
              </View>
            </View>
            {canShowTemporaryToggle ? (
              <Pressable
                style={[
                  styles.temporaryButton,
                  { borderColor: palette.temporaryBorder, backgroundColor: palette.temporaryBg },
                  row.temporarySelected && { backgroundColor: palette.temporaryActiveBg, borderColor: palette.temporaryActiveBg },
                ]}
                onPress={() => onToggleTemporary(row.rowId)}
                accessibilityLabel={labels.temporaryToggleA11y}
                disabled={saving || row.status === 'pending'}
              >
                <Text
                  style={[
                    styles.temporaryButtonText,
                    { color: palette.temporaryText },
                    row.temporarySelected && { color: palette.temporaryActiveText },
                  ]}
                >
                  {row.temporarySelected ? labels.temporarySelected : labels.createTemporary}
                </Text>
              </Pressable>
            ) : null}
            {rowOpen === row.rowId && row.status !== 'pending' ? (
              <View
                style={[
                  styles.dropdown,
                  { borderColor: palette.border, backgroundColor: palette.dropdownBg },
                  actionsOutsideFrame && styles.dropdownWithActionsOffset,
                ]}
              >
                {options.length > 0 ? (
                  options.map((member) => (
                    <Pressable
                      key={member.id}
                      style={styles.option}
                      onPress={() => {
                        clearDraftBlurTimeout();
                        onDraftSelectOption(row.rowId, member);
                      }}
                    >
                      <Text style={[styles.optionText, { color: palette.text }]}>{member.name}</Text>
                    </Pressable>
                  ))
                ) : (
                  <View style={styles.optionEmpty}>
                    <Text style={[styles.optionText, { color: palette.text }]}>{labels.noResults ?? ''}</Text>
                  </View>
                )}
              </View>
            ) : null}
          </View>
        );
      })}
      {showAddButton ? (
        <Pressable
          style={[
            styles.addButton,
            isCompact && styles.addButtonCompact,
            { borderColor: palette.addBorder, backgroundColor: palette.addBg },
            !canAdd && styles.disabled,
          ]}
          onPress={onAddRow}
          disabled={!canAdd}
        >
          <Text style={[styles.addButtonText, isCompact && styles.addButtonTextCompact, { color: palette.addText }]}>
            {labels.addPlayer}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 10,
  },
  rowWrap: {
    gap: 6,
  },
  frameWithActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  membershipFrame: {
    flex: 1,
  },
  existingInlineRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingRight: 12,
  },
  readonlyInput: {
    flex: 1,
    minHeight: 42,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    justifyContent: 'center',
  },
  readonlyInputCompact: {
    minHeight: 38,
  },
  existingRow: {
    borderWidth: 1,
    borderRadius: 12,
    minHeight: 42,
    paddingHorizontal: 12,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  existingRowCompact: {
    minHeight: 38,
    paddingVertical: 7,
  },
  existingText: {
    fontSize: 16,
    fontWeight: '700',
    flex: 1,
  },
  aiAvatar: {
    marginRight: 6,
  },
  existingTextReadonly: {
    flex: 0,
    lineHeight: 22,
  },
  existingTextCompact: {
    fontSize: 13,
    fontWeight: '600',
  },
  removeButton: {
    minWidth: 104,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
    borderWidth: 1,
  },
  removeButtonText: {
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
  },
  removeButtonCompact: {
    minWidth: 82,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  removeButtonIcon: {
    minWidth: 26,
    width: 26,
    height: 26,
    paddingHorizontal: 0,
    paddingVertical: 0,
    borderRadius: 8,
  },
  removeButtonIconCompact: {
    minWidth: 26,
    width: 26,
    height: 26,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  removeButtonTextCompact: {
    fontSize: 10,
  },
  removeButtonTextIcon: {
    fontSize: 13,
    lineHeight: 15,
  },
  draftInputWrap: {
    position: 'relative',
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  draftInputWrapOutside: {
    paddingRight: 12,
  },
  draftInput: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 12,
    minHeight: 42,
    paddingHorizontal: 12,
    paddingVertical: 8,
    paddingRight: 78,
    fontSize: 15,
    fontWeight: '600',
  },
  draftInputWithInline: {
    paddingRight: 12,
  },
  draftInputOutside: {
    minHeight: 42,
    paddingVertical: 0,
  },
  draftInputCompact: {
    minHeight: 38,
    paddingVertical: 7,
    fontSize: 13,
  },
  draftActions: {
    position: 'absolute',
    right: 8,
    top: 7,
    flexDirection: 'row',
    gap: 6,
  },
  draftActionsOutside: {
    position: 'relative',
    right: 0,
    top: 0,
    width: 58,
    justifyContent: 'center',
  },
  confirmButton: {
    width: 26,
    height: 26,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#15803d',
    borderWidth: 1,
    borderColor: '#A1D493',
  },
  confirmButtonText: {
    color: '#dcfce7',
    fontWeight: '800',
    fontSize: 14,
    lineHeight: 16,
  },
  cancelButton: {
    width: 26,
    height: 26,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#7f1d1d',
    borderWidth: 1,
    borderColor: '#ef4444',
  },
  cancelButtonText: {
    color: '#fee2e2',
    fontWeight: '700',
    fontSize: 13,
    lineHeight: 16,
  },
  dropdown: {
    borderWidth: 1,
    borderRadius: 10,
    overflow: 'hidden',
  },
  dropdownWithActionsOffset: {
    marginRight: 66,
  },
  option: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  optionEmpty: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    opacity: 1,
  },
  optionText: {
    fontSize: 14,
    fontWeight: '600',
  },
  temporaryButton: {
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  temporaryButtonText: {
    fontSize: 11,
    fontWeight: '700',
  },
  addButton: {
    marginTop: 4,
    borderWidth: 1,
    borderRadius: 14,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButtonCompact: {
    paddingVertical: 8,
  },
  addButtonText: {
    fontWeight: '700',
    fontSize: 16,
  },
  addButtonTextCompact: {
    fontSize: 13,
  },
  disabled: {
    opacity: 0.5,
  },
});
